import React, { Fragment, useEffect, useState } from "react";
import ModalBox from "../../components/modal/ModalBox";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { toastMessage } from "../../utils/toastMessage";
import {
  getSelectedConsultantDetailV1,
  editConsultantV1,
} from "../../services/consultantManagement";
import { fatchCategoryApi } from "../../services/categoryManagement";
import { allowFloatingNumber, allowNumber, optimizeFunction, regex } from "../../utils/regex";
import commonMessages from "../../utils/commonMessages";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IconButton, Chip, Box } from "@mui/material";
import {
  CommonSelect,
  EditCommonSelect,
  Input,
  TextArea,
} from "../../components/Form";
import PhoneInput from "react-phone-input-2";
import {
  languages,
} from "../../utils/genderList";
import { dateFormat, dateFormatYear } from "../../utils/dateFormat";
import createMediaData from "../../utils/mediaDataSender";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import LocalizedNameFields, { namesFromConsultant } from "./LocalizedNameFields";
import scrollToUp from "../../utils/scrollToUp";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import ConsultantProfileAssests from "../../components/imagePreview/Consultant-assests";

const { IMAGE_FORMAT } = commonMessages;
const { imageFileRegex } = regex;

// Indian States
const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi (National Capital Territory of Delhi)",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const stateOptions = indianStates.map((state) => ({
  value: state,
  label: state,
}));

const peopleAskMeQuestionGroups = [
  {
    category: "Relationship",
    questions: [
      "Kya mera partner mujhse sachcha pyaar karta hai?",
      "Kya mera relationship long-term chalega?",
      "Kya mera ex wapas aayega?",
      "Mere relationship mein itni problems kyun aa rahi hain?",
      "Kya mujhe is relationship ko continue karna chahiye?",
      "Kya mera partner loyal hai?",
      "Kya hamare beech misunderstanding khatam hogi?",
      "Kya mera breakup permanent hai?",
      "Kya meri love life mein stability aayegi?",
      "Kya mujhe apne partner se shaadi karni chahiye?",
      "Kya mera partner future ko lekar serious hai?",
      "Relationship mein trust issue kab solve hoga?",
      "Kya meri life mein new love entry hogi?",
      "Kya long distance relationship successful hoga?",
      "Kya hum dono compatible hain?",
    ],
  },
  {
    category: "Career",
    questions: [
      "Meri job kab lagegi?",
      "Job change karna mere liye sahi rahega?",
      "Mere career mein growth kab hogi?",
      "Job mein promotion kab milega?",
      "Kya mujhe government job milegi?",
      "Private job mere liye better hai ya government job?",
      "Meri salary kab increase hogi?",
      "Career mein baar-baar struggle kyun aa raha hai?",
      "Kya mujhe foreign job ka chance milega?",
      "Mere liye kaunsa career field best rahega?",
      "Kya current job mere future ke liye sahi hai?",
      "Kya mujhe job ke liye city change karni chahiye?",
      "Interview clear hoga ya nahi?",
      "Career mein success kab milegi?",
      "Kya meri kundli mein stable career ka yog hai?",
    ],
  },
  {
    category: "Marriage",
    questions: [
      "Meri shaadi kab hogi?",
      "Love marriage hogi ya arranged marriage?",
      "Kya meri shaadi mein delay hai?",
      "Mere life partner ka nature kaisa hoga?",
      "Kya meri shaadi successful rahegi?",
      "Kya meri kundli mein marriage problem hai?",
      "Shaadi ke liye sahi time kab hai?",
      "Kya meri shaadi family ki marzi se hogi?",
      "Kya meri partner se compatibility achhi rahegi?",
      "Kya meri shaadi same caste mein hogi ya different caste mein?",
      "Kya meri second marriage ka yog hai?",
      "Kya manglik dosh shaadi mein problem karega?",
      "Kya meri marriage life mein separation ka chance hai?",
      "Kya meri shaadi ke baad life better hogi?",
      "Kya jis person se baat chal rahi hai, usse shaadi hogi?",
    ],
  },
  {
    category: "Business",
    questions: [
      "Job karu ya khud ka business start karu?",
      "Kya business mere liye lucky hai?",
      "Business start karne ka sahi time kab hai?",
      "Kaunsa business mere liye best rahega?",
      "Kya partnership business mere liye sahi hai?",
      "Mere business mein profit kab aayega?",
      "Business mein loss kyun ho raha hai?",
      "Kya mujhe apna current business continue karna chahiye?",
      "Kya business expand karna sahi rahega?",
      "Kya mujhe family business join karna chahiye?",
      "Business partner trustworthy hai ya nahi?",
      "Kya foreign business ya import-export ka chance hai?",
      "Business mein investment karna safe rahega?",
      "Kya online business mere liye successful hoga?",
      "Kya meri kundli mein business success ka yog hai?",
    ],
  },
];

const peopleAskMeQuestions = peopleAskMeQuestionGroups.flatMap((group) => group.questions);

const topicGroups = [
  {
    name: "Relationships & Marriage",
    descriptions: ["Toxic Relationships", "Heartbreak", "Divorce", "Second marriage"],
  },
  {
    name: "Affairs, Trust & Emotional Pain",
    descriptions: ["Extra-marital affairs", "Loyalty doubts", "Still attached to ex"],
  },
  {
    name: "Mental & Emotional Well-Being",
    descriptions: ["Overthinking", "Depression feelings"],
  },
  {
    name: "Spiritual Journey",
    descriptions: ["Spiritual talks"],
  },
  {
    name: "Intimacy & Personal Matters",
    descriptions: ["Sex life Issues"],
  },
  {
    name: "Family & Home Affairs",
    descriptions: ["Family disputes", "In-laws problems", "Joint family conflicts", "Property disputes"],
  },
  {
    name: "Private & Sensitive Matters",
    descriptions: [
      "Secret relationships",
      "Hidden relationships",
      "Things you can't share with family",
      "Things you can't tell friends",
      "Guilt & regret",
    ],
  },
];

const topicNames = topicGroups.map((group) => group.name);
const ratingParamTitles = ["Patience", "Prediction accuracy", "Privacy", "Emotional Support", "Analysis"];

const groupTopicsForEditor = (items = []) => {
  const grouped = [];
  items.forEach((topic) => {
    const name = topic?.name || "";
    const descriptions = Array.isArray(topic?.descriptions)
      ? topic.descriptions
      : topic?.description
        ? [topic.description]
        : [];
    const existing = grouped.find((item) => item.name === name);
    if (existing) {
      descriptions.forEach((description) => {
        if (!existing.descriptions.includes(description)) existing.descriptions.push(description);
      });
    } else {
      grouped.push({ name, descriptions: [...descriptions] });
    }
  });
  return grouped;
};

const expandTopicsForSave = (items = []) =>
  items.flatMap((topic) =>
    (topic.descriptions || []).map((description) => ({ name: topic.name, description }))
  );

const EditConsultantV1 = () => {
  // For local validation error states
  const [questionAnswerError, setQuestionAnswerError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const preSelectedReturnState = location.state?.preSelectedReturn;
  const backToPreviousPage = () => {
    if (preSelectedReturnState?.submittedFormId) {
      navigate("/pre-selected-consultant", {
        state: { preSelectedReturn: preSelectedReturnState },
      });
      return;
    }
    navigate(-1);
  };
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  
  // Basic Info States
  const [consultantDetail, setConsultantDetail] = useState({});
  const [profileImage, setProfileImage] = useState("");
  const [username, setUsername] = useState("");
  const [localizedNames, setLocalizedNames] = useState({ namesEn: "", namesHi: "", namesKn: "" });
  const [languageValue, setLanguageValue] = useState([]);
  const [primarySkills, setPrimarySkills] = useState([]);
  const [experienceYear, setExperienceYear] = useState("");
  const [price, setPrice] = useState({ default: "", discount: "", platformShare: "" });
  const [aboutMe, setAboutMe] = useState("");
  const [ranking, setRanking] = useState();
  const [serviceModes, setServiceModes] = useState({ chat: "", audio: "" });
  
  // Personal Details States
  const [currentlyLivingIn, setCurrentlyLivingIn] = useState("");
  
  // Media States
  const [gallery, setGallery] = useState([]);
  // Changed to [] for proper required logic:
  const [podcast, setPodcast] = useState([]);
  const [clientStories, setClientStories] = useState([]);
  const [skillBasedPublications, setSkillBasedPublications] = useState([]);
  const [certificates, setCertificates] = useState([]);
  
  // Content States
  // Changed to [] for proper logic:
  const [questionAnswer, setQuestionAnswer] = useState([]);
  const [topics, setTopics] = useState([]);
  const [ratingParams, setRatingParams] = useState([]);
const [tag, setTag] = useState("");
  
  // Options States
  const [consultantSkillOption, setConsultantSkillOption] = useState([]);
  const [languageOptions, setLanguageOption] = useState([]);
  const [consultantSkillLoader, setConsultantSkillLoader] = useState(false);
  const [buttonref, buttonLoader] = useButtonLoader("Update");

  // Fetch consultant details
  const getSelectedConsultantDetailHandler = async () => {
    try {
      showLoader();
      const sendData = { consultantId: id };
      const { data } = await getSelectedConsultantDetailV1(sendData);

      if (data.code === 200) {
        const result = data?.result;
        setConsultantDetail(result);
        
        // Set profile image
        if (result?.profileImage) {
          setProfileImage(result.profileImage);
        }
        
        // Set gallery
        if (result?.gallery && result.gallery.length > 0) {
          setGallery(result.gallery);
        }
        
        setUsername(result?.accountName || "");
        setLocalizedNames(namesFromConsultant(result));
        setLanguageValue(
          (result?.language || []).map((item) => ({
            label: item,
            value: item,
          }))
        );
        setPrimarySkills(
          (result?.primarySkills || []).map((item) => ({
            label: item,
            value: item,
          }))
        );
        setExperienceYear(result?.experienceYear || "");
        setPrice({
          default: result?.price?.default || "",
          discount: result?.price?.discount || "",
          platformShare: result?.price?.platformShare !== undefined && result?.price?.platformShare !== null ? String(result.price.platformShare) : "",
        });
        setRanking(result?.Ranking || 999999);
        setServiceModes({
          chat: typeof result?.serviceModes?.chat === "number" ? result.serviceModes.chat : "",
          audio: typeof result?.serviceModes?.audio === "number" ? result.serviceModes.audio : "",
        });
        
        // Set aboutMe from bio
        if (result?.bio) {
          setAboutMe(result.bio || "");
        }
        
        // Personal Details
        setCurrentlyLivingIn(result?.details?.state || "");
        setTag(result?.details?.tag || "");
        
        // Certificates
        if (result?.details?.certificates && result.details.certificates.length > 0) {
          setCertificates(result.details.certificates);
        }
        
        // Podcast
        if (result?.podcast && result.podcast.length > 0) {
          setPodcast(result.podcast);
        }
        
        // Client Stories
        if (result?.clientStories && result.clientStories.length > 0) {
          setClientStories(result.clientStories);
        }
        
        // Question Answer
        if (result?.questionAnswer && result.questionAnswer.length > 0) {
          setQuestionAnswer(result.questionAnswer);
        }
        
        // Topics
        if (result?.topics && result.topics.length > 0) {
          setTopics(groupTopicsForEditor(result.topics));
        }
        
        // Rating Params
        if (result?.ratingParams && result.ratingParams.length > 0) {
          setRatingParams(result.ratingParams);
        } else if (result?.details?.RatingParams && result.details.RatingParams.length > 0) {
          // Handle old format
          const oldParams = result.details.RatingParams[0];
          setRatingParams([
            { title: "Prediction", value: oldParams.Prediction || "", color: "#ff0000" },
            { title: "Solution", value: oldParams.Solution || "", color: "#00ff00" },
            { title: "Privacy", value: oldParams.Privacy || "", color: "#0000ff" },
            { title: "Patience", value: oldParams.Patience || "", color: "#ffff00" },
          ]);
        }
        
        
        scrollToUp();
      } else {
        toastMessage(data.message, "error", "consultantDetail");
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to fetch consultant details", "error");
    } finally {
      hideLoader();
    }
  };

  // Profile Image Handler
  const profileImageHandler = (e) => {
    let { files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      if (file && imageFileRegex.test(file.name)) {
        setProfileImage(file);
      } else {
        e.target.value = "";
        setProfileImage("");
        toastMessage(IMAGE_FORMAT, "error", "imageFormat");
      }
    } else {
      e.target.value = "";
      setProfileImage("");
    }
  };

  // Fetch consultant skill and language options
  const fetchConsultantSkillHandler = async () => {
    try {
      setConsultantSkillLoader(true);
      const { data } = await fatchCategoryApi();
      if (data.code === 200) {
        const updatedConsultantSkill =
          data?.result?.skillList &&
          data?.result?.skillList.length > 0 &&
          data?.result?.skillList.map(({ _id, title }) => ({
            value: title,
            label: title,
          }));
        setConsultantSkillOption(updatedConsultantSkill || []);

        const language =
          data?.result?.languageList &&
          data?.result?.languageList.length > 0 &&
          data.result.languageList.map(({ _id, title }) => ({
            value: title,
            label: title,
          }));
        setLanguageOption(language || []);
      } else {
        setConsultantSkillOption([]);
        setLanguageOption([]);
        toastMessage(data.message, "error", "categoryList");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setConsultantSkillLoader(false);
    }
  };

  // Gallery Handler
  const galleryHandler = (e) => {
    const { files } = e.target;
    if (files.length > 0) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(file => imageFileRegex.test(file.name));
      if (validFiles.length > 0) {
        setGallery(prev => [...prev, ...validFiles]);
      }
      e.target.value = "";
    }
  };

  // Remove gallery image
  const removeGalleryImage = (index) => {
    setGallery(prev => prev.filter((_, i) => i !== index));
  };

  // Add new podcast
  const addPodcast = () => {
    setPodcast([...podcast, { name: "", link: "" }]);
  };

  // Remove podcast
  const removePodcast = (index) => {
    setPodcast(prev => prev.filter((_, i) => i !== index));
  };

  // Update podcast
  const updatePodcast = (index, field, value) => {
    const updated = [...podcast];
    updated[index][field] = value;
    setPodcast(updated);
  };

  // Add new client story
  const addClientStory = () => {
    setClientStories([...clientStories, { name: "", duration: "", image: "", audio: "" }]);
  };

  // Remove client story
  const removeClientStory = (index) => {
    setClientStories(prev => prev.filter((_, i) => i !== index));
  };

  // Update client story
  const updateClientStory = (index, field, value) => {
    const updated = [...clientStories];
    updated[index][field] = value;
    setClientStories(updated);
  };

  // Client story image handler
  const clientStoryImageHandler = (index, e) => {
    const { files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      if (file && imageFileRegex.test(file.name)) {
        updateClientStory(index, "image", file);
      } else {
        toastMessage(IMAGE_FORMAT, "error", "imageFormat");
      }
    }
    e.target.value = "";
  };

  // Client story audio handler
  const clientStoryAudioHandler = (index, e) => {
    const { files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      updateClientStory(index, "audio", file);
    }
    e.target.value = "";
  };

  // Add new question answer
  const addQuestionAnswer = () => {
    setQuestionAnswer([...questionAnswer, { question: "", answer: "" }]);
  };

  // Remove question answer
  const removeQuestionAnswer = (index) => {
    setQuestionAnswer(prev => prev.filter((_, i) => i !== index));
  };

  // Update question answer
  const updateQuestionAnswer = (index, field, value) => {
    const updated = [...questionAnswer];
    updated[index][field] = value;
    setQuestionAnswer(updated);
  };

  // Add new topic
  const addTopic = () => {
    setTopics([...topics, { name: "", descriptions: [] }]);
  };

  // Remove topic
  const removeTopic = (index) => {
    setTopics(prev => prev.filter((_, i) => i !== index));
  };

  // Update topic
  const updateTopic = (index, field, value) => {
    const updated = [...topics];
    updated[index][field] = value;
    if (field === "name") {
      updated[index].descriptions =
        topicGroups.find((group) => group.name === value)?.descriptions || [];
    }
    setTopics(updated);
  };

  // Add new rating param
  const addRatingParam = () => {
    setRatingParams([...ratingParams, { title: "", value: "", color: "#000000" }]);
  };

  // Remove rating param
  const removeRatingParam = (index) => {
    setRatingParams(prev => prev.filter((_, i) => i !== index));
  };

  // Update rating param
  const updateRatingParam = (index, field, value) => {
    const updated = [...ratingParams];
    updated[index][field] = value;
    if (field === "title" && value === "Privacy") {
      updated[index].value = "100";
    }
    setRatingParams(updated);
  };



  // Add new certificate
  const addCertificate = () => {
    setCertificates([...certificates, { icon: "", name: "", institute: "", image: "" }]);
  };

  // Remove certificate
  const removeCertificate = (index) => {
    setCertificates(prev => prev.filter((_, i) => i !== index));
  };

  // Update certificate
  const updateCertificate = (index, field, value) => {
    const updated = [...certificates];
    updated[index][field] = value;
    setCertificates(updated);
  };

  // Certificate icon handler
  const certificateIconHandler = (index, e) => {
    const { files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      if (file && imageFileRegex.test(file.name)) {
        updateCertificate(index, "icon", file);
      } else {
        toastMessage(IMAGE_FORMAT, "error", "imageFormat");
      }
    }
    e.target.value = "";
  };

  // Certificate image handler
  const certificateImageHandler = (index, e) => {
    const { files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      if (file && imageFileRegex.test(file.name)) {
        updateCertificate(index, "image", file);
      } else {
        toastMessage(IMAGE_FORMAT, "error", "imageFormat");
      }
    }
    e.target.value = "";
  };

  // Update Consultant Handler
  const updateConsultantHandler = async () => {
    setQuestionAnswerError(null);
    // local validation for questionAnswer
    if (questionAnswer.length > 0) {
      const invalidQA = questionAnswer.find(
        qa => !qa.question?.trim()
      );
      if (invalidQA) {
        setQuestionAnswerError('Please fill Question in all rows.');
        return;
      }
    }
    try {
      buttonLoader(true);
      const formData = new FormData();
      
      // Profile Image
      if (profileImage && profileImage.name) {
        formData.append("profileImage", profileImage);
      }
      
      // Basic Info
      formData.append("consultantId", id);
      formData.append("username", username);
      formData.append("namesEn", localizedNames.namesEn || username || "");
      formData.append("namesHi", localizedNames.namesHi || "");
      formData.append("namesKn", localizedNames.namesKn || "");

      if (ranking) {
        formData.append("Ranking", parseInt(ranking));
      }
      if (serviceModes.chat !== "") formData.append("chat", serviceModes.chat);
      if (serviceModes.audio !== "") formData.append("audio", serviceModes.audio);
      
      if (languageValue && languageValue.length > 0) {
        languageValue.forEach(lang => {
          formData.append("language", lang.value);
        });
      }
      
      if (primarySkills && primarySkills.length > 0) {
        primarySkills.forEach(skill => {
          formData.append("primarySkills", skill.value);
        });
      }
      
      formData.append("experienceYear", experienceYear);
      formData.append("priceDefault", price.default);
      formData.append("priceDiscount", price.discount);
      if (price.platformShare !== "" && price.platformShare != null) {
        formData.append("pricePlatformShare", price.platformShare);
      }
      
      // About Me
      formData.append("aboutMe", aboutMe || "");
      
      // Personal Details
      formData.append("currentlyLivingIn", currentlyLivingIn);
      
      // Gallery
      if (gallery && gallery.length > 0) {
        gallery.forEach((file, index) => {
          if (file instanceof File) {
            formData.append(`gallery`, file);
          }
        });
      }
      
      // Podcast
      formData.append("podcast", JSON.stringify(podcast.length > 0 ? podcast : []));
      
      // Client Stories
      clientStories.forEach((story, index) => {
        if (story.image instanceof File) {
          formData.append(`clientStoryImage_${index}`, story.image);
        }
        if (story.audio instanceof File) {
          formData.append(`clientStoryAudio_${index}`, story.audio);
        }
      });
      formData.append("clientStories", JSON.stringify(clientStories.length > 0 ? clientStories : []));
      
      // Certificates
      certificates.forEach((cert, index) => {
        if (cert.icon instanceof File) {
          formData.append(`certificateIcon_${index}`, cert.icon);
        }
        if (cert.image instanceof File) {
          formData.append(`certificateImage_${index}`, cert.image);
        }
      });
      formData.append("certificates", JSON.stringify(certificates.length > 0 ? certificates : []));
      
      // Question Answer
      formData.append("questionAnswer", JSON.stringify(questionAnswer.length > 0 ? questionAnswer : []));
      
      // Topics
      formData.append("topics", JSON.stringify(expandTopicsForSave(topics)));
      
      // Rating Params
      formData.append("ratingParams", JSON.stringify(ratingParams.length > 0 ? ratingParams : []));
      
// Details (with Tag)
const details = {
  state: currentlyLivingIn,
  certificates
};
formData.append("details", JSON.stringify(details));
formData.append("tag", tag || "");
      
      const data = await editConsultantV1(formData);

      if (data?.data.code === 200) {
        toastMessage(data.data?.message, "success", "updateConsultant");
        backToPreviousPage();
      } else {
        toastMessage(data.data?.message, "error", "updateConsultant");
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to update consultant", "error");
    } finally {
      buttonLoader(false);
    }
  };

  useEffect(() => {
    fetchConsultantSkillHandler();
    getSelectedConsultantDetailHandler();
  }, []);

  return (
    <Fragment>
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <div className="user_detail_heading">
              <button
                type="button"
                className="back_icon"
                onClick={backToPreviousPage}
                style={{ background: "transparent", border: 0, padding: 0 }}
              >
                <IconButton>
                  <ArrowBackIosNewIcon className="back_button" />
                </IconButton>
              </button>
              <h4 className="Title">Edit Consultant Profile (V1)</h4>
            </div>
          </div>
          
          <div className="row" style={{ marginBottom: '10px' }}>
            {/* Profile Image */}
            <div className="col-3">
              <div>
                <label className="my-1 text-dark" style={{ marginBottom: '5px', fontSize: '14px' }}>
                  Profile Image <span className="text-danger">*</span>
                </label>
              </div>
              <input
                type="file"
                onChange={profileImageHandler}
                accept="image/*"
                className="form-control"
                style={{ fontSize: '13px', padding: '5px' }}
              />
              {(profileImage || consultantDetail?.profileImage) && (
                <Avatar
                  alt=""
                  sx={{ width: 100, height: 100, mt: 1 }}
                  src={
                    profileImage && profileImage.name
                      ? URL.createObjectURL(profileImage)
                      : profileImage || consultantDetail?.profileImage
                  }
                />
              )}
            </div>

            {/* Username */}
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="username"
                  value={username}
                  required
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter Username"
                  className="form-control"
                  label="Username"
                  maxLength={30}
                />
              </div>
            </div>

            <LocalizedNameFields
              values={localizedNames}
              columnClass="col-3"
              onChange={(e) => {
                const { name, value } = e.target;
                setLocalizedNames((prev) => ({ ...prev, [name]: value }));
              }}
            />

            {/* Primary Skills */}
            <div className="col-3">
              <div className="form-group select_sm_width mb-1">
                <EditCommonSelect
                  className="add-consultant"
                  label="Primary Skills"
                  options={consultantSkillOption}
                  onChange={setPrimarySkills}
                  initialSelectedValues={primarySkills}
                  loading={consultantSkillLoader}
                  isMulti={true}
                  isSearchable={true}
                  maxOptions={3}
                  required
                />
              </div>
            </div>

            {/* Language */}
            <div className="col-3">
              <div className="form-group select_sm_width mb-1">
                <EditCommonSelect
                  className="add-consultant"
                  label="Language"
                  options={languageOptions}
                  onChange={setLanguageValue}
                  initialSelectedValues={languageValue}
                  isMulti={true}
                  isSearchable={true}
                  required
                />
              </div>
            </div>

            {/* Experience Year */}
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="experienceYear"
                  value={experienceYear}
                  required
                  onChange={(e) => setExperienceYear(e.target.value)}
                  placeholder="Experience Year"
                  className="form-control"
                  label="Experience Year"
                />
              </div>
            </div>

            {/* Price Default */}
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="priceDefault"
                  value={price.default}
                  required
                  onChange={(e) => setPrice({ ...price, default: e.target.value })}
                  placeholder="Default Price"
                  className="form-control"
                  label="Price (Default)"
                />
              </div>
            </div>

            {/* Price Discount */}
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="priceDiscount"
                  value={price.discount}
                  onChange={(e) => setPrice({ ...price, discount: e.target.value })}
                  placeholder="Discount Price"
                  className="form-control"
                  label="Price (Discount)"
                />
              </div>
            </div>

            {/* Platform Share (%) */}
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="platformShare"
                  value={price.platformShare}
                  onChange={(e) => setPrice({ ...price, platformShare: e.target.value })}
                  placeholder="Platform Share %"
                  className="form-control"
                  label="Platform Share (%)"
                />
                <small className="text-muted" style={{ fontSize: '11px' }}>Platform share percentage (e.g. 30, 50)</small>
              </div>
            </div>
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="ranking"
                  value={ranking}
                  onChange={(e) => setRanking(e.target.value)}
                  placeholder="Ranking"
                  className="form-control"
                  label="Ranking"
                />
              </div>
            </div>
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="chat"
                  value={serviceModes.chat}
                  onChange={(e) => setServiceModes((prev) => ({ ...prev, chat: e.target.value }))}
                  placeholder="Chat"
                  className="form-control"
                  label="Chat"
                />
              </div>
            </div>
            <div className="col-3">
              <div className="form-group mb-1">
                <Input
                  type="text"
                  name="audio"
                  value={serviceModes.audio}
                  onChange={(e) => setServiceModes((prev) => ({ ...prev, audio: e.target.value }))}
                  placeholder="Audio"
                  className="form-control"
                  label="Audio"
                />
              </div>
            </div>

            {/* About Me */}
            <div className="col-12">
              <div className="form-group mb-1">
                <TextArea
                  type="text"
                  name="aboutMe"
                  value={aboutMe}
                  required
                  onChange={(e) => setAboutMe(e.target.value)}
                  placeholder="About Me..."
                  className="form-control"
                  label="About Me"
                />
                <small className="text-muted" style={{ fontSize: '11px' }}>First 3 lines visible, rest in "More About Me"</small>
              </div>
            </div>

            {/* Personal Details Section */}
            <div className="col-12" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <h6 className="text-dark" style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>Personal Details</h6>
            </div>

            {/* Currently Living In */}
            <div className="col-6">
              <div className="form-group select_sm_width mb-1">
                <EditCommonSelect
                  className="add-consultant"
                  label="Currently Living In"
                  options={stateOptions}
                  onChange={(val) => setCurrentlyLivingIn(val?.value || "")}
                  initialSelectedValues={currentlyLivingIn ? { value: currentlyLivingIn, label: currentlyLivingIn } : null}
                  isMulti={false}
                  required
                />
              </div>
            </div>

        

            {/* Podcast */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="text-dark" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Podcast</label>
                  <button type="button" className="btn btn-sm btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={addPodcast}>
                    + Add
                  </button>
                </div>
                {podcast.map((pod, index) => (
                  <div key={index} className="border p-2 mb-2" style={{ borderRadius: '4px' }}>
                    <div className="row">
                      <div className="col-5">
                        <Input
                          type="text"
                          value={pod.name}
                          onChange={(e) => updatePodcast(index, "name", e.target.value)}
                          placeholder="Podcast Name"
                          className="form-control"
                          label="Name"
                        />
                      </div>
                      <div className="col-5">
                        <Input
                          type="text"
                          value={pod.link}
                          onChange={(e) => updatePodcast(index, "link", e.target.value)}
                          placeholder="YouTube Embed Link"
                          className="form-control"
                          label="Link"
                        />
                      </div>
                      <div className="col-2">
                        <IconButton
                          onClick={() => removePodcast(index)}
                          color="error"
                          size="small"
                          sx={{ mt: 3 }}
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Client Stories */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="text-dark" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Client Stories</label>
                  <button type="button" className="btn btn-sm btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={addClientStory}>
                    + Add
                  </button>
                </div>
                {clientStories.map((story, index) => (
                  <div key={index} className="border p-2 mb-2" style={{ borderRadius: '4px' }}>
                    <div className="row">
                      <div className="col-3">
                        <Input
                          type="text"
                          value={story.name}
                          onChange={(e) => updateClientStory(index, "name", e.target.value)}
                          placeholder="Story Name"
                          className="form-control"
                          label="Name"
                        />
                      </div>
                      <div className="col-2">
                        <Input
                          type="text"
                          value={story.duration}
                          onChange={(e) => updateClientStory(index, "duration", e.target.value)}
                          placeholder="Duration"
                          className="form-control"
                          label="Duration"
                        />
                      </div>
                      <div className="col-3">
                        <label className="text-dark" style={{ fontSize: '13px', marginBottom: '3px' }}>Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => clientStoryImageHandler(index, e)}
                          className="form-control"
                          style={{ fontSize: '12px', padding: '3px' }}
                        />
                        {story.image && (
                          <div className="mt-1">
                            {story.image instanceof File ? (
                              <img
                                src={URL.createObjectURL(story.image)}
                                alt="Preview"
                                style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px" }}
                              />
                            ) : (
                              <img
                                src={story.image}
                                alt="Preview"
                                style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px" }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <div className="col-3">
                        <label className="text-dark" style={{ fontSize: '13px', marginBottom: '3px' }}>Audio</label>
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={(e) => clientStoryAudioHandler(index, e)}
                          className="form-control"
                          style={{ fontSize: '12px', padding: '3px' }}
                        />
                        {story.audio && (
                          <div className="mt-1">
                            <audio controls style={{ width: "100%", height: "30px" }}>
                              <source src={story.audio instanceof File ? URL.createObjectURL(story.audio) : story.audio} />
                            </audio>
                          </div>
                        )}
                      </div>
                      <div className="col-1">
                        <IconButton
                          onClick={() => removeClientStory(index)}
                          color="error"
                          size="small"
                          sx={{ mt: 3 }}
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certificates */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="text-dark" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Certificates</label>
                  <button type="button" className="btn btn-sm btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={addCertificate}>
                    + Add
                  </button>
                </div>
                {certificates.map((cert, index) => (
                  <div key={index} className="border p-2 mb-2" style={{ borderRadius: '4px' }}>
                    <div className="row">
                      <div className="col-2">
                        <label className="text-dark" style={{ fontSize: '13px', marginBottom: '3px' }}>Icon</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => certificateIconHandler(index, e)}
                          className="form-control"
                          style={{ fontSize: '12px', padding: '3px' }}
                        />
                        {cert.icon && (
                          <div className="mt-1">
                            <img
                              src={cert.icon instanceof File ? URL.createObjectURL(cert.icon) : cert.icon}
                              alt="Icon"
                              style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="col-3">
                        <Input
                          type="text"
                          value={cert.name}
                          onChange={(e) => updateCertificate(index, "name", e.target.value)}
                          placeholder="Certificate Name"
                          className="form-control"
                          label="Name"
                        />
                      </div>
                      <div className="col-3">
                        <Input
                          type="text"
                          value={cert.institute}
                          onChange={(e) => updateCertificate(index, "institute", e.target.value)}
                          placeholder="Institute"
                          className="form-control"
                          label="Institute"
                        />
                      </div>
                      <div className="col-3">
                        <label className="text-dark" style={{ fontSize: '13px', marginBottom: '3px' }}>Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => certificateImageHandler(index, e)}
                          className="form-control"
                          style={{ fontSize: '12px', padding: '3px' }}
                        />
                        {cert.image && (
                          <div className="mt-1">
                            <img
                              src={cert.image instanceof File ? URL.createObjectURL(cert.image) : cert.image}
                              alt="Preview"
                              style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px" }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="col-1">
                        <IconButton
                          onClick={() => removeCertificate(index)}
                          color="error"
                          size="small"
                          sx={{ mt: 3 }}
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Question Answer */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="text-dark" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>People Ask Me</label>
                  <button type="button" className="btn btn-sm btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={addQuestionAnswer}>
                    + Add
                  </button>
                </div>
                {questionAnswer.length === 0 ? null : (
                  questionAnswer.map((qa, index) => (
                    <div key={index} className="border p-2 mb-2" style={{ borderRadius: '4px' }}>
                      <div className="row">
                        <div className="col-10">
                          <label className="text-dark" style={{ fontSize: '12px', marginBottom: '4px' }}>
                            Question <span style={{ color: 'red' }}>*</span>
                          </label>
                          <select
                            value={qa.question}
                            onChange={(e) => updateQuestionAnswer(index, "question", e.target.value)}
                            className="form-control"
                            required
                          >
                            <option value="">Select Question</option>
                            {qa.question && !peopleAskMeQuestions.includes(qa.question) && (
                              <option value={qa.question}>{qa.question}</option>
                            )}
                            {peopleAskMeQuestionGroups.map((group) => (
                              <optgroup key={group.category} label={group.category}>
                                {group.questions.map((question) => (
                                  <option key={question} value={question}>
                                    {question}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                        <div className="col-2">
                          <IconButton
                            onClick={() => removeQuestionAnswer(index)}
                            color="error"
                            size="small"
                            sx={{ mt: 3 }}
                            aria-label="delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {questionAnswerError && (
                  <div style={{ color: 'red', fontSize: 12 }}>
                    {questionAnswerError}
                  </div>
                )}
              </div>
            </div>

            {/* Topics */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="text-dark" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Topics</label>
                  <button type="button" className="btn btn-sm btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={addTopic}>
                    + Add
                  </button>
                </div>
                {topics.length === 0 ? (
                  <div style={{ textAlign: 'right' }}>
                    {/* Only + Add button is visible by default (already above) */}
                  </div>
                ) : (
                  topics.map((topic, index) => (
                    <div key={index} className="border p-2 mb-2" style={{ borderRadius: '4px' }}>
                      <div className="row">
                        <div className="col-4">
                          <label className="text-dark" style={{ fontSize: '12px', marginBottom: '4px' }}>
                            Name <span style={{ color: 'red' }}>*</span>
                          </label>
                          <select
                            value={topic.name}
                            onChange={(e) => updateTopic(index, "name", e.target.value)}
                            className="form-control"
                            required
                          >
                            <option value="">Select Topic</option>
                            {topic.name && !topicNames.includes(topic.name) && (
                              <option value={topic.name}>{topic.name}</option>
                            )}
                            {topicGroups.map((group) => (
                              <option key={group.name} value={group.name}>
                                {group.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="text-dark" style={{ fontSize: '12px', marginBottom: '4px' }}>
                            Description
                          </label>
                          <div
                            className="form-control"
                            style={{ minHeight: '38px', height: 'auto', backgroundColor: '#f8f9fa' }}
                          >
                            {topic.descriptions?.length
                              ? topic.descriptions.join(", ")
                              : "Select a topic to include all descriptions"}
                          </div>
                        </div>
                        <div className="col-2">
                          <IconButton
                            onClick={() => removeTopic(index)}
                            color="error"
                            size="small"
                            sx={{ mt: 3 }}
                            aria-label="delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Rating Params */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="text-dark" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Rating Params</label>
                  <button type="button" className="btn btn-sm btn-primary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={addRatingParam}>
                    + Add
                  </button>
                </div>
                {ratingParams.map((param, index) => (
                  <div key={index} className="border p-2 mb-2" style={{ borderRadius: '4px' }}>
                    <div className="row">
                      <div className="col-4">
                        <label className="text-dark" style={{ fontSize: '12px', marginBottom: '4px' }}>
                          Title
                        </label>
                        <select
                          value={param.title}
                          onChange={(e) => updateRatingParam(index, "title", e.target.value)}
                          className="form-control"
                        >
                          <option value="">Select Title</option>
                          {param.title && !ratingParamTitles.includes(param.title) && (
                            <option value={param.title}>{param.title}</option>
                          )}
                          {ratingParamTitles.map((title) => (
                            <option key={title} value={title}>
                              {title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-3">
                        <Input
                          type="text"
                          value={param.value}
                          onChange={(e) => updateRatingParam(index, "value", e.target.value)}
                          placeholder="Value"
                          className="form-control"
                          label="Value"
                        />
                      </div>
                      <div className="col-3">
                        <Input
                          type="color"
                          value={param.color}
                          onChange={(e) => updateRatingParam(index, "color", e.target.value)}
                          className="form-control"
                          label="Color"
                        />
                      </div>
                      <div className="col-2">
                        <IconButton
                          onClick={() => removeRatingParam(index)}
                          color="error"
                          size="small"
                          sx={{ mt: 3 }}
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tag */}
            <div className="col-12" style={{ marginTop: '10px' }}>
              <div className="form-group mb-1">
                <label className="text-dark" style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Tag</label>
                <Input
                  type="text"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder="Enter Tag (e.g. Open Minded)"
                  className="form-control"
                />
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              className="btn_size text-center"
              onClick={updateConsultantHandler}
              ref={buttonref}
            >
              Update
            </button>
          </div>
        </div>
        <ConsultantProfileAssests consultantID={id} consultantDetail={consultantDetail || {}} />
      </div>
    </Fragment>
  );
};

export default EditConsultantV1;

